import { type KeyboardEvent, type ReactElement, useRef, useState } from 'react'

import { FvButton, TextAreaField, ValidatedForm } from '@fv/client-components'
import { assetUrl, shipperUri } from '@fv/client-core'

import { ShipperPopup } from '@/components/ShipperPopup'

import { useHasAskAboutShipment } from './api'
import { useStartChatSession } from './hooks'

type Props = {
  contextMessage: string
  trigger: ReactElement
  placeholder?: string
  submitLabel?: string
  side?: 'right' | 'left' | 'top' | 'bottom'
}

export const buildShipmentContextMessage = (loadId: string) =>
  `Regarding [this shipment](${shipperUri}/details/${loadId}) ...`

export const PjPromptPopup = ({
  contextMessage,
  trigger,
  placeholder = 'Ask a question about this shipment...',
  submitLabel = 'Ask Pallet Jack',
  side = 'right',
}: Props) => {
  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  const { startChat, isLoading: isStartingChat } = useStartChatSession()
  const hasAsk = useHasAskAboutShipment()

  const handleValidSubmit = () => {
    const trimmed = question.trim()
    if (!trimmed) return

    startChat(`${contextMessage}\n\n${trimmed}`)
    setQuestion('')
    setOpen(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      formRef.current?.requestSubmit()
    }
  }

  if (!hasAsk) {
    return null
  }

  return (
    <ShipperPopup
      open={open}
      onOpenChange={setOpen}
      positionerProps={{ side, align: 'center' }}
      trigger={trigger}
    >
      <ValidatedForm
        ref={formRef}
        className="flex flex-col gap-2 w-72"
        onValidSubmit={handleValidSubmit}
      >
        <TextAreaField
          name="pj-prompt"
          className="form-control p-2 resize-none min-h-24"
          placeholder={placeholder}
          value={question}
          onChange={event => setQuestion(event.target.value)}
          onKeyDown={handleKeyDown}
          required
        />
        <FvButton
          type="submit"
          theme="white"
          loading={isStartingChat}
          buttonTextClass="gap-x-2 flex items-center"
        >
          <img
            src={assetUrl('pj/pj.svg')}
            alt="Pallet Jack"
            className="w-6 inline-block"
          />
          <span>{submitLabel}</span>
        </FvButton>
      </ValidatedForm>
    </ShipperPopup>
  )
}
